import { useEffect, useRef, useState } from "react";
import { Box, Flex, TextField } from "@radix-ui/themes";
import { SearchIcon } from "@/assets/icons";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { analytics, EventEnum } from "@/lib/analytics";
import type { ListCreatorDto } from "@/lib/lutely-api/gen/dtos";
import { useSearchResults } from "@/hooks/useSearchResults";
import SearchDropdown from "./SearchDropdown";

type SearchFieldProps = {
  isDark: boolean;
};

export default function SearchField({ isDark }: SearchFieldProps) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const marketplaceSearchTarget =
    pathname === "/marketplace/songs" || pathname === "/marketplace/albums"
      ? pathname
      : "/marketplace/albums";
  const { albums, songs, creators, hasResults, isLoading, canSearch } =
    useSearchResults(search);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [isOpen]);

  useEffect(() => {
    setSearch("");
    setIsOpen(false);
  }, [pathname]);

  const goTo = (to: string, params?: Record<string, string>) => {
    setIsOpen(false);
    setSearch("");
    navigate({ to, params });
  };

  // Branded /@slug first; the raw id URL only exists for creators without a
  // slug (LUT-927).
  const handleSelectCreator = (creator: ListCreatorDto) => {
    if (creator.slug) {
      goTo("/@{$CREATOR_SLUG}", { CREATOR_SLUG: creator.slug });
    } else {
      goTo("/creators/$CREATOR_ID", { CREATOR_ID: creator.creatorId });
    }
  };

  const handleSelectAlbum = (albumId: string) => {
    goTo("/albums/$ALBUM_ID", { ALBUM_ID: albumId });
  };

  const handleSelectSong = (trackId: string) => {
    goTo("/tracks/$TRACK_ID", { TRACK_ID: trackId });
  };

  function handleSearch() {
    if (search !== "") {
      analytics.track(EventEnum.MARKETPLACE_FILTER_SELECTED, {
        searchQuery: search,
      });
      navigate({ to: marketplaceSearchTarget, search: { query: search } });
      setSearch("");
      setIsOpen(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      handleSearch();
    }
  }

  return (
    <Box ref={rootRef} className="relative w-full max-w-[500px]">
      <Flex
        className={cn(
          "h-10 w-full items-center gap-1 rounded-full px-4 transition-colors overflow-hidden border-[1px]",
          isDark
            ? "bg-[rgba(255,255,255,0.30)] border-[#878E9A]"
            : "border-[#DEE3EA]",
        )}
      >
        <SearchIcon
          className={cn(
            "w-[20px] flex-shrink-0",
            isDark ? "fill-white/80" : "fill-[#525966]",
          )}
        />
        <TextField.Root
          className={cn(
            "flex-1 h-10 border-0 bg-transparent font-semibold outline-none shadow-none focus:outline-none focus:ring-0",
            isDark ? "sOutlinedDark" : "sOutlined",
          )}
          placeholder="Search albums, creators, songs"
          variant="soft"
          aria-label="Search albums, creators, songs"
          value={search}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsOpen(true);
          }}
          inputMode="search"
          autoComplete="off"
        />
      </Flex>

      <SearchDropdown
        show={isOpen && canSearch}
        albums={albums}
        songs={songs}
        creators={creators}
        hasResults={hasResults}
        isLoading={isLoading}
        onSelectCreator={handleSelectCreator}
        onSelectAlbum={handleSelectAlbum}
        onSelectSong={handleSelectSong}
      />
    </Box>
  );
}
